import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, Fx, Pt, RoundProps, ViewProps } from '../kit.js';
import { BOARD, BoardFrame, INK, OK, TYPE, clamp, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { balanceOf, dealScale } from './logic.js';
import type { Scale, Side } from './logic.js';
import { CRATE_PAL, shade } from './shared.js';

/**
 * QUARTERMASTER · BALANCE THE SCALES — weights on the pans until the beam is level.
 *
 * A load sits on one pan; numbered brass weights stand on the shelf below. Tap a weight to lift
 * it, then tap a pan to set it there (or drag it straight onto a pan); tap a weight on a pan, or
 * its empty place on the shelf, to take it off again. The beam leans to the heavier side and each
 * pan shows its total; when the two are equal the beam comes level and locks. Weighing is trial
 * and error, so nothing here is a mistake. Round 1 is three small weights, two of which make the
 * load; round 2 three a little bigger; round 3 four bigger ones, two or three of which make it.
 * (A round where a weight must go beside the load as well is still in the rules; no round deals it
 * since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): the beam's swing as plain functions, a small view state
 * (`ScalesView`), a pure `View`, the `Board` that plays it, and the `demo` the how-to card plays.
 */

const W = BOARD.w;
/** The scales stand in the top of the board, on a counter; the shelf of weights is below. */
const SCENE_H = 222;
const PIVOT_Y = 62;
/** From the pivot to each end of the beam. */
const BEAM_HALF = 100;
/** From a beam end down to its pan's rim. */
const HANG = 66;
const PAN_HALF = 54;
/** The shelf: touches below `TRAY_Y` are on the weights; the weights stand on `SHELF_Y`. */
const TRAY_Y = SCENE_H + 2;
const SHELF_Y = 304;
/** A weight's size on a pan, and on the shelf, against its drawing. */
const ON_PAN = 0.95;
const ON_SHELF = 1.2;
/** How far a finger moves before a press on a weight becomes a drag, in units. */
const DRAG = 10;

/** A weight's drawn size for its number: heavier is a little bigger. */
const weightSize = (value: number) => ({ w: 28 + value * 0.75, h: 24 + value * 0.62 });
const LOAD_W = 46;
const LOAD_H = 42;

/** How far the beam leans for a difference: a little for 1, never past 13°. */
export function leanFor(diff: number): number {
  if (diff === 0) return 0;
  return Math.sign(diff) * Math.min(13, 3 + Math.abs(diff) * 0.8);
}

export interface Beam {
  /** Degrees, right side down. */
  angle: number;
  /** Degrees a second. */
  speed: number;
}

/** One frame of the beam: a damped spring towards its lean. */
export function swingBeam(beam: Beam, lean: number, dt: number): void {
  beam.speed += ((lean - beam.angle) * 34 - beam.speed * 7.5) * dt;
  beam.angle += beam.speed * dt;
}

/** The scales are level and still enough to lock. */
export const settled = (diff: number, beam: Beam) => diff === 0 && Math.abs(beam.angle) < 0.4 && Math.abs(beam.speed) < 4;

/** How the pans swing on their hooks as the beam moves. */
const swingOf = (beam: Beam) => clamp(-beam.speed * 0.22, -6, 6);

/** Where each weight is, as a view state writes it: one letter a weight, '-' on the shelf. */
export const placedCode = (placed: readonly (Side | null)[]) => placed.map((p) => p ?? '-').join('');
const placedOf = (code: string): (Side | null)[] => Array.from(code, (ch) => (ch === 'L' || ch === 'R' ? ch : null));

/** Where a pan's hook is, for the beam at `angle` (degrees, right side down). */
function hookOf(side: Side, angle: number) {
  const r = (angle * Math.PI) / 180;
  const k = side === 'R' ? 1 : -1;
  return { x: W / 2 + k * BEAM_HALF * Math.cos(r), y: PIVOT_Y + k * BEAM_HALF * Math.sin(r) };
}

interface PanItem {
  /** −1 for the load, else the weight's index. */
  i: number;
  /** Centre across the pan, and the size it is drawn at. */
  cx: number;
  k: number;
  w: number;
  h: number;
}

/** What stands on a pan, left to right, squeezed to fit if it has to. */
function panItems(scale: Pick<Scale, 'loadSide' | 'weights'>, placed: readonly (Side | null)[], side: Side): PanItem[] {
  const items: { i: number; w: number; h: number }[] = [];
  if (scale.loadSide === side) items.push({ i: -1, w: LOAD_W, h: LOAD_H });
  placed.forEach((p, i) => {
    if (p !== side) return;
    const { w, h } = weightSize(scale.weights[i]!);
    items.push({ i, w: w * ON_PAN, h: h * ON_PAN });
  });
  const gap = 3;
  const total = items.reduce((s, it) => s + it.w, 0) + gap * Math.max(0, items.length - 1);
  const k = Math.min(1, (PAN_HALF * 2 - 10) / Math.max(1, total));
  let x = (-total * k) / 2;
  return items.map((it) => {
    const cx = x + (it.w * k) / 2;
    x += (it.w + gap) * k;
    return { ...it, cx, k };
  });
}

/** The shelf's slots: where weight `i` of `n` stands below the scales. */
const slotX = (i: number, n: number) => 12 + ((W - 24) / n) * (i + 0.5);
/** The slot under a finger at `x`, or null. */
function slotAt(x: number, n: number): number | null {
  const i = Math.floor(((x - 12) / (W - 24)) * n);
  return i >= 0 && i < n ? i : null;
}

/** Everything the scales show. */
export interface ScalesView {
  /** The game clock, ms. */
  t: number;
  /** The load, and the pan it sits on. */
  l: number;
  ls: Side;
  /** The weights' numbers, and where each is: one letter a weight, '-' on the shelf, 'L' or 'R' on a pan. */
  w: readonly number[];
  p: string;
  /** The weight lifted off the shelf to be set on a pan (−1 none). */
  sel: number;
  /** A weight being dragged: which, and where the finger is (units). */
  d: readonly [i: number, x: number, y: number] | null;
  /** The beam's lean in degrees (right side down), and how the pans swing. */
  a: number;
  sw: number;
  /** Level and locked. */
  done: boolean;
  fx?: Fx | undefined;
}

/** One brass weight, standing on 0,0. */
function Weight({ value, id, ghost = false }: { value: number; id: string; ghost?: boolean }) {
  const { w, h } = weightSize(value);
  const body = `M${-w / 2} 0 L${-w / 2 + w * 0.1} ${-h * 0.72} Q${-w / 2 + w * 0.13} ${-h * 0.8} ${-w / 2 + w * 0.22} ${-h * 0.8} H${w / 2 - w * 0.22} Q${w / 2 - w * 0.13} ${-h * 0.8} ${w / 2 - w * 0.1} ${-h * 0.72} L${w / 2} 0 Z`;
  if (ghost) {
    return <path d={body} fill="none" stroke={INK.faint} strokeWidth="1.2" strokeDasharray="3 3" />;
  }
  const fs = Math.min(16, w * 0.44);
  return (
    <g>
      <ellipse cx="0" cy="1" rx={w * 0.56} ry="3" fill="#000" opacity=".45" />
      <path d={body} fill={`url(#${id}brass)`} stroke="#5a4210" strokeWidth="1" />
      <rect x={-w * 0.13} y={-h * 0.95} width={w * 0.26} height={h * 0.17} rx="1.5" fill={`url(#${id}brass)`} stroke="#5a4210" strokeWidth=".8" />
      <ellipse cx="0" cy={-h * 0.96} rx={w * 0.2} ry={h * 0.07} fill="#f5de96" stroke="#5a4210" strokeWidth=".8" />
      <path d={`M${-w / 2 + 3} -2.5 H${w / 2 - 3}`} stroke="#fff3c4" strokeOpacity=".35" strokeWidth="1" />
      <text x="0" y={-h * 0.36 + fs * 0.36} textAnchor="middle" fontSize={fs} fontWeight="700" fontFamily={mono} fill="#3a2806">
        {value}
      </text>
    </g>
  );
}

/** The load, standing on 0,0: water under fire, a sack under the sand, a crate anywhere else. */
function Load({ era, value, id }: { era: Era; value: number; id: string }) {
  const pal = CRATE_PAL[era];
  const plate = (y: number) => (
    <g>
      <rect x="-14" y={y} width="28" height="17" rx="3" fill="#f3ead2" stroke="#00000055" />
      <text x="0" y={y + 13} textAnchor="middle" fontSize={TYPE.label} fontWeight="700" fontFamily={mono} fill="#1b1408">
        {value}
      </text>
    </g>
  );
  if (era === 'debris') {
    return (
      <g>
        <ellipse cx="0" cy="1" rx="24" ry="3" fill="#000" opacity=".45" />
        <path d="M-20 0 V-34 Q-20 -40 -14 -40 H8 L14 -46 H20 V0 Z" fill="#4b6574" stroke="#1d2a31" strokeWidth="1.2" />
        <rect x="-15" y="-47" width="16" height="7" rx="2" fill="none" stroke="#9fb7c4" strokeWidth="2.2" />
        <rect x="14" y="-50" width="6" height="5" rx="1" fill="#c9d6dd" />
        <path d="M-20 -30 H20" stroke="#6f8c9b" strokeWidth="1" />
        <path d="M-12 -8 L12 -8" stroke="#00000055" />
        <path d="M-10 -19 q2 -5 4 -8 q2 3 4 8 a4 4 0 0 1 -8 0 z" fill="#7fd0ff" transform="translate(-1 1)" opacity=".9" />
        <g transform="translate(6 0)">{plate(-27)}</g>
      </g>
    );
  }
  if (era === 'dust') {
    return (
      <g>
        <ellipse cx="0" cy="1" rx="24" ry="3" fill="#000" opacity=".45" />
        <path d="M-21 0 Q-25 -22 -14 -36 L-8 -40 H8 L14 -36 Q25 -22 21 0 Z" fill="#b8895a" stroke="#5c3c1c" strokeWidth="1.2" />
        <path d="M-9 -40 Q0 -45 9 -40 L6 -46 Q0 -49 -6 -46 Z" fill="#a37748" stroke="#5c3c1c" />
        <path d="M-10 -39 H10" stroke="#e7c48c" strokeWidth="2" />
        <path d="M-16 -6 Q0 -2 16 -6" stroke="#8a6034" fill="none" />
        {plate(-27)}
      </g>
    );
  }
  return (
    <g>
      <ellipse cx="0" cy="1" rx="25" ry="3" fill="#000" opacity=".45" />
      <rect x={-LOAD_W / 2} y={-LOAD_H} width={LOAD_W} height={LOAD_H} rx="3" fill={`url(#${id}cargo)`} stroke={pal.edge} strokeWidth="1.2" />
      <rect x={-LOAD_W / 2 + 0.6} y={-LOAD_H + 0.6} width={LOAD_W - 1.2} height="7" rx="2.5" fill={pal.top} />
      <path d={`M${-LOAD_W / 2 + 4} ${-LOAD_H + 8} V-3 M${LOAD_W / 2 - 4} ${-LOAD_H + 8} V-3`} stroke={pal.edge} strokeWidth="2" opacity=".5" />
      {era === 'neon' && <rect x={-LOAD_W / 2 + 1.5} y={-LOAD_H + 1.5} width={LOAD_W - 3} height={LOAD_H - 3} rx="2.5" fill="none" stroke={pal.edge} opacity=".55" />}
      {era === 'canopy' && (
        <g>
          <ellipse cx="-9" cy={-LOAD_H - 2} rx="7" ry="3.2" fill="#7fc86a" transform={`rotate(-20 -9 ${-LOAD_H - 2})`} />
          <ellipse cx="6" cy={-LOAD_H - 3} rx="7" ry="3.2" fill="#9ad874" transform={`rotate(16 6 ${-LOAD_H - 3})`} />
        </g>
      )}
      {plate(-27)}
    </g>
  );
}

/** The scales, from their view state alone: the beam, the pans and their totals, and the shelf of weights. */
function View({ view, era, accent, lang, input }: ViewProps<ScalesView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const pal = CRATE_PAL[era];
  const cx = W / 2;
  const scale = { load: view.l, loadSide: view.ls, weights: view.w };
  const placed = placedOf(view.p);
  const diff = balanceOf(scale, placed);
  // A demo glides every number between its keys: the lifted weight is read rounded.
  const sel = Math.round(view.sel);
  const drag = view.d;
  const dragged = drag ? Math.round(drag[0]) : -1;
  const done = view.done;
  const choosing = (sel >= 0 || drag !== null) && !done;
  const angle = view.a;
  const totals: Record<Side, number> = {
    L: (view.ls === 'L' ? view.l : 0) + view.w.reduce((s, w, i) => s + (placed[i] === 'L' ? w : 0), 0),
    R: (view.ls === 'R' ? view.l : 0) + view.w.reduce((s, w, i) => s + (placed[i] === 'R' ? w : 0), 0),
  };
  const n = view.w.length;
  const status = done
    ? { text: say(lang, '✓ LEVEL', '✓ I VATER'), tone: 'ok' as const }
    : choosing
      ? { text: say(lang, 'NOW TAP A PAN', 'TRYKK PÅ EN SKÅL') }
      : diff === 0
        ? { text: say(lang, 'LEVEL…', 'I VATER…') }
        : { text: say(lang, 'MAKE IT LEVEL', 'FÅ VEKTEN I VATER'), hint: say(lang, 'WEIGHT, THEN PAN', 'LODD, SÅ SKÅL') };

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}brass`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#7c5a1c" />
          <stop offset=".3" stopColor="#f2d489" />
          <stop offset=".6" stopColor="#cfa54c" />
          <stop offset="1" stopColor="#6a4a14" />
        </linearGradient>
        <linearGradient id={`${id}steel`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#b8c4ce" />
          <stop offset=".5" stopColor="#6d7a86" />
          <stop offset="1" stopColor="#353f49" />
        </linearGradient>
        <linearGradient id={`${id}post`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#2c353f" />
          <stop offset=".45" stopColor="#8795a2" />
          <stop offset="1" stopColor="#222a33" />
        </linearGradient>
        <linearGradient id={`${id}pan`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e9cf86" />
          <stop offset="1" stopColor="#8a6424" />
        </linearGradient>
        <linearGradient id={`${id}cargo`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(pal.body, 0.18)} />
          <stop offset="1" stopColor={shade(pal.body, -0.3)} />
        </linearGradient>
        <linearGradient id={`${id}shelf`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={shade(pal.top, -0.1)} />
          <stop offset="1" stopColor={shade(pal.body, -0.45)} />
        </linearGradient>
        <radialGradient id={`${id}lamp`} cx=".5" cy="0" r="1">
          <stop offset="0" stopColor={skin.haze} stopOpacity=".22" />
          <stop offset="1" stopColor={skin.haze} stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx={cx} cy="0" rx={W * 0.6} ry={SCENE_H} fill={`url(#${id}lamp)`} />
      {/* the counter the scales stand on */}
      <rect x="0" y={SCENE_H - 8} width={W} height="10" fill="#000" opacity=".3" />
      <line x1="0" y1={SCENE_H - 8} x2={W} y2={SCENE_H - 8} stroke={skin.ink} strokeOpacity=".35" />

      {/* the post, its foot, and the level gauge above the pivot */}
      <path d={`M${cx - 36} ${SCENE_H - 8} L${cx - 24} ${SCENE_H - 22} H${cx + 24} L${cx + 36} ${SCENE_H - 8} Z`} fill={`url(#${id}steel)`} stroke="#1a2027" />
      <rect x={cx - 5} y={PIVOT_Y} width="10" height={SCENE_H - 22 - PIVOT_Y} fill={`url(#${id}post)`} />
      <g stroke="#8795a2" strokeWidth="1.2" fill="none">
        <path d={`M${cx - 40 * Math.sin(0.42)} ${PIVOT_Y - 40 * Math.cos(0.42)} A40 40 0 0 1 ${cx + 40 * Math.sin(0.42)} ${PIVOT_Y - 40 * Math.cos(0.42)}`} opacity=".6" />
        {[-3, -2, -1, 1, 2, 3].map((k) => {
          const a = (k * 8 * Math.PI) / 180;
          return <line key={k} x1={cx + 36 * Math.sin(a)} y1={PIVOT_Y - 36 * Math.cos(a)} x2={cx + 41 * Math.sin(a)} y2={PIVOT_Y - 41 * Math.cos(a)} opacity=".6" />;
        })}
      </g>
      <line x1={cx} y1={PIVOT_Y - 33} x2={cx} y2={PIVOT_Y - 45} stroke={accent} strokeWidth="2.4" strokeLinecap="round" />

      {/* the beam, leaning to the heavier side, with its needle */}
      <g transform={`rotate(${angle.toFixed(2)} ${cx} ${PIVOT_Y})`}>
        <line x1={cx} y1={PIVOT_Y} x2={cx} y2={PIVOT_Y - 38} stroke={done ? OK : '#f2f6f9'} strokeWidth="2" strokeLinecap="round" />
        <path d={`M${cx - 4} ${PIVOT_Y - 34} L${cx} ${PIVOT_Y - 42} L${cx + 4} ${PIVOT_Y - 34} Z`} fill={done ? OK : '#f2f6f9'} />
        <rect x={cx - BEAM_HALF - 5} y={PIVOT_Y - 4.5} width={BEAM_HALF * 2 + 10} height="9" rx="4.5" fill={`url(#${id}steel)`} stroke="#1a2027" strokeWidth="1" />
        <line x1={cx - BEAM_HALF + 6} y1={PIVOT_Y} x2={cx + BEAM_HALF - 6} y2={PIVOT_Y} stroke={done ? OK : skin.ink} strokeOpacity={done ? 1 : 0.7} strokeWidth="1.6" />
        <circle cx={cx - BEAM_HALF} cy={PIVOT_Y} r="3.4" fill="#dfe6ec" stroke="#1a2027" />
        <circle cx={cx + BEAM_HALF} cy={PIVOT_Y} r="3.4" fill="#dfe6ec" stroke="#1a2027" />
      </g>
      <circle cx={cx} cy={PIVOT_Y} r="9" fill={`url(#${id}steel)`} stroke="#1a2027" />
      {done ? (
        // Locked: a clasp over the pivot.
        <g transform={`translate(${cx} ${PIVOT_Y})`}>
          <path d="M-4 -2 V-6 A4 4 0 0 1 4 -6 V-2" fill="none" stroke={INK.onFill} strokeWidth="1.8" />
          <rect x="-5.5" y="-2.5" width="11" height="8" rx="1.5" fill={OK} stroke={INK.onFill} strokeWidth="1" />
        </g>
      ) : (
        <circle cx={cx} cy={PIVOT_Y} r="2.6" fill="#1a2027" />
      )}

      {/* the two pans, hanging level whatever the beam does */}
      {(['L', 'R'] as const).map((side) => {
        const hook = hookOf(side, angle);
        const items = panItems(scale, placed, side);
        const heavier = totals[side] > totals[side === 'L' ? 'R' : 'L'];
        return (
          <g key={side} transform={`translate(${hook.x.toFixed(2)} ${hook.y.toFixed(2)}) rotate(${view.sw.toFixed(2)})`}>
            {choosing && <ellipse cx="0" cy={HANG + 4} rx={PAN_HALF + 8} ry="16" fill={accent} opacity=".16" />}
            <path d={`M0 0 L${-PAN_HALF + 8} ${HANG} M0 0 L${PAN_HALF - 8} ${HANG}`} stroke="#aab6c0" strokeOpacity=".75" strokeWidth="1.1" />
            <circle cx="0" cy="0" r="2.6" fill="#dfe6ec" />
            {items.map((it) => (
              <g key={it.i} transform={`translate(${it.cx.toFixed(2)} ${HANG + 3}) scale(${it.k.toFixed(3)})`}>
                {it.i < 0 ? (
                  <Load era={era} value={view.l} id={id} />
                ) : (
                  <g transform={`scale(${ON_PAN})`}>
                    <Weight value={view.w[it.i]!} id={id} />
                  </g>
                )}
              </g>
            ))}
            <path d={`M${-PAN_HALF} ${HANG} Q0 ${HANG + 28} ${PAN_HALF} ${HANG} Z`} fill={`url(#${id}pan)`} stroke={choosing ? accent : '#5a4210'} strokeWidth={choosing ? 1.8 : 1} />
            <ellipse cx="0" cy={HANG} rx={PAN_HALF} ry="3.2" fill="#f5e2a8" opacity=".8" />
            {/* the pan's total */}
            <g transform={`translate(0 ${HANG + 24})`}>
              <rect x="-20" y="0" width="40" height="21" rx="5" fill="#070a0e" stroke={done ? OK : heavier ? INK.text : INK.faint} strokeWidth="1.2" />
              <text x="0" y="15.5" textAnchor="middle" fontSize={TYPE.label + 1} fontWeight="700" fontFamily={mono} fill={done ? OK : INK.hi}>
                {totals[side]}
              </text>
            </g>
          </g>
        );
      })}

      {/* the shelf of weights */}
      <rect x="6" y={SHELF_Y} width={W - 12} height="9" rx="2" fill={`url(#${id}shelf)`} />
      <line x1="6" y1={SHELF_Y + 0.5} x2={W - 6} y2={SHELF_Y + 0.5} stroke={shade(pal.top, 0.2)} strokeOpacity=".7" />
      {view.w.map((value, i) => {
        const away = placed[i] !== null || dragged === i;
        const lifted = sel === i;
        return (
          <g key={i} transform={`translate(${slotX(i, n).toFixed(1)} ${SHELF_Y - (lifted ? 10 : 0)})`}>
            {lifted && <ellipse cx="0" cy={10} rx="30" ry="6" fill={accent} opacity=".35" />}
            <g transform={`scale(${ON_SHELF})`}>
              <Weight value={value} id={id} ghost={away} />
            </g>
            {placed[i] !== null && (
              <text x="0" y="-16" textAnchor="middle" fontSize={TYPE.hint} fontWeight="700" fontFamily={mono} fill={INK.dim}>
                {placed[i] === 'L' ? '◀' : '▶'}
              </text>
            )}
            {lifted && <path d={`M-24 ${-weightSize(value).h * ON_SHELF - 8} h48`} stroke={accent} strokeWidth="2" strokeLinecap="round" />}
          </g>
        );
      })}

      {/* a weight on its way, under the finger */}
      {drag && view.w[dragged] !== undefined && (
        <g transform={`translate(${drag[1].toFixed(1)} ${(drag[2] - 18).toFixed(1)}) scale(${ON_SHELF})`}>
          <Weight value={view.w[dragged]!} id={id} />
        </g>
      )}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const [scale] = useState(() => dealScale(level, seeded(Date.now() + level * 7919)));
  const n = scale.weights.length;
  const placed = useRef<(Side | null)[]>(scale.weights.map(() => null));
  const sel = useRef<number | null>(null);
  const drag = useRef<{ i: number; x: number; y: number } | null>(null);
  const pressed = useRef<{ i: number; x: number; y: number } | null>(null);
  // The beam starts leaning under the load, and swings on a damped spring from there.
  const [beam] = useState<Beam>(() => ({ angle: leanFor(balanceOf(scale, placed.current)), speed: 0 }));

  const round = useRound(props, (dt, _now, won) => {
    const diff = balanceOf(scale, placed.current);
    swingBeam(beam, leanFor(diff), dt);
    if (won) return;
    round.progress(Math.max(0, Math.min(0.9, 1 - Math.abs(diff) / scale.load)));
    if (settled(diff, beam)) {
      sel.current = null;
      drag.current = null;
      pressed.current = null;
      round.win();
    }
  });

  // Covered: whatever was in hand goes back to the shelf.
  useEffect(() => {
    if (!props.paused) return;
    pressed.current = null;
    drag.current = null;
    sel.current = null;
  }, [props.paused]);

  const put = (i: number, side: Side | null) => {
    placed.current = placed.current.map((s, j) => (j === i ? side : s));
  };

  const down = (p: Pt) => {
    if (!round.live) return;
    if (p.y >= TRAY_Y) {
      const i = slotAt(p.x, n);
      if (i === null) return;
      if (placed.current[i] !== null) {
        // Its empty place on the shelf: take it off the pan.
        put(i, null);
        sel.current = null;
        return;
      }
      pressed.current = { i, x: p.x, y: p.y };
      return;
    }
    const side: Side = p.x < W / 2 ? 'L' : 'R';
    if (sel.current !== null) {
      put(sel.current, side);
      sel.current = null;
      return;
    }
    // A weight on a pan, tapped: off it comes.
    const hook = hookOf(side, beam.angle);
    let best: { i: number; d: number } | null = null;
    for (const it of panItems(scale, placed.current, side)) {
      if (it.i < 0) continue;
      const d = Math.hypot(p.x - (hook.x + it.cx), p.y - (hook.y + HANG - (it.h * it.k) / 2));
      if (d < 40 && (!best || d < best.d)) best = { i: it.i, d };
    }
    if (best) put(best.i, null);
  };

  const move = (p: Pt) => {
    const from = pressed.current;
    if (!from || !round.live) return;
    if (drag.current || Math.hypot(p.x - from.x, p.y - from.y) > DRAG) {
      sel.current = null;
      drag.current = { i: from.i, x: p.x, y: p.y };
    }
  };

  const up = () => {
    const from = pressed.current;
    pressed.current = null;
    if (!from || !round.live) return;
    const d = drag.current;
    if (d) {
      if (d.y < TRAY_Y - 6) put(from.i, d.x < W / 2 ? 'L' : 'R');
      drag.current = null;
      return;
    }
    sel.current = sel.current === from.i ? null : from.i;
  };

  const d = drag.current;
  const view: ScalesView = {
    t: q(round.now),
    l: scale.load,
    ls: scale.loadSide,
    w: scale.weights,
    p: placedCode(placed.current),
    sel: sel.current ?? -1,
    d: d ? [d.i, q(d.x), q(d.y)] : null,
    a: q(beam.angle, 1),
    sw: q(swingOf(beam), 1),
    done: round.won,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Scales and weights', 'Vekt og lodd'), down, move, up }}
    />
  );
}

/**
 * The how-to, weighed by the board's own beam: a load of 8 on the left, and 3, 5 and 4 on the
 * shelf. The finger lifts the 3 and sets it on the right pan, then the 5; the beam swings level
 * and locks. Worked out once with the spring and keyed every tenth of a second.
 */
function demoWeighing(): DemoScript<ScalesView> {
  const scale = { load: 8, loadSide: 'L' as const, weights: [3, 5, 4] };
  const n = scale.weights.length;
  const events: readonly { at: number; sel?: number; put?: [number, Side] }[] = [
    { at: 1_000, sel: 0 },
    { at: 1_900, put: [0, 'R'] },
    { at: 3_000, sel: 1 },
    { at: 3_900, put: [1, 'R'] },
  ];
  const ms = 7_000;
  const placed: (Side | null)[] = scale.weights.map(() => null);
  let sel = -1;
  let next = 0;
  const beam: Beam = { angle: leanFor(balanceOf(scale, placed)), speed: 0 };
  let doneAt: number | null = null;
  const keys: DemoKey<ScalesView>[] = [];
  const dt = 1 / 60;
  for (let t = 0, frame = 0; t <= ms; t += dt * 1000, frame++) {
    while (next < events.length && events[next]!.at <= t + 1) {
      const e = events[next++]!;
      if (e.sel !== undefined) sel = e.sel;
      if (e.put) {
        placed[e.put[0]] = e.put[1];
        sel = -1;
      }
    }
    const diff = balanceOf(scale, placed);
    if (doneAt === null) {
      swingBeam(beam, leanFor(diff), dt);
      if (settled(diff, beam)) doneAt = t;
    } else swingBeam(beam, 0, dt);
    if (frame % 6 === 0) {
      const glow = doneAt === null ? null : Math.max(0, 1 - (t - doneAt) / 450);
      keys.push({
        at: Math.round(t),
        view: {
          t: 0,
          l: scale.load,
          ls: scale.loadSide,
          w: scale.weights,
          p: placedCode(placed),
          sel,
          d: null,
          a: q(beam.angle, 1),
          sw: q(swingOf(beam), 1),
          done: doneAt !== null,
          ...(glow === null ? {} : { fx: [q(glow, 2), 0] as const }),
        },
      });
    }
  }
  const weightAt = (i: number) => ({ x: q(slotX(i, n)), y: SHELF_Y - 16 });
  const pan = { x: 262, y: 150 };
  const tap = (at: number, p: { x: number; y: number }) => [
    { at: at - 250, x: p.x, y: p.y },
    { at, x: p.x, y: p.y, down: true },
    { at: at + 150, x: p.x, y: p.y, down: false },
  ];
  return {
    ms,
    keys,
    finger: [
      { at: 0, x: 250, y: 304, hide: true },
      { at: 300, x: 250, y: 304, hide: false },
      ...tap(1_000, weightAt(0)),
      ...tap(1_900, pan),
      ...tap(3_000, weightAt(1)),
      ...tap(3_900, pan),
      // Off the board soon after, so the two totals show as they come level.
      { at: 4_500, x: 290, y: 300, hide: true },
    ],
    steps: [
      { at: 0, en: 'TAP A WEIGHT', no: 'TRYKK PÅ ET LODD' },
      { at: 1_400, en: 'THEN TAP A PAN', no: 'TRYKK SÅ PÅ EN SKÅL' },
      { at: 4_100, en: 'MAKE BOTH SIDES EQUAL', no: 'GJØR BEGGE SIDER LIKE' },
    ],
  };
}

export const game = thirtyGame<ScalesView>({
  Board,
  View,
  demo: demoWeighing(),
  mistakes: false,
  words: {
    en: {
      instruction: 'Put weights on the empty pan until the scales are level. Not every weight is needed.',
      howMove: 'Tap a weight, then tap a pan to put it there. Tap it again to take it off.',
    },
    no: {
      instruction: 'Legg lodd i den tomme skålen til vekten er i vater. Du trenger ikke alle.',
      howMove: 'Trykk på et lodd, så på en skål for å legge det der. Trykk igjen for å ta det av.',
    },
  },
});
